const INTERNAL_METADATA_PREFIX = 'highstakes__';

function normalizeKey(key: string) {
  return key.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

const PRIORITY_KEYS = new Map(
  [
    'correlationId',
    'resourceType',
    'resourceId',
    'customerId',
    'userId',
    'environment',
    'env',
    'region',
  ].map((key, index) => [normalizeKey(key), index]),
);

function truncate(value: string, length: number) {
  return value.length > length ? `${value.slice(0, length)}…` : value;
}

export function formatMetadataValue(value: unknown, maxLength = 50): string {
  if (value === null) {
    return 'null';
  }
  if (value === undefined) {
    return 'undefined';
  }

  const serialized =
    typeof value === 'string'
      ? value
      : typeof value === 'object'
        ? JSON.stringify(value)
        : String(value);

  return truncate(serialized ?? String(value), maxLength);
}

export function formatMetadataPreviewValue(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.length} ${value.length === 1 ? 'item' : 'items'}]`;
  }
  if (value && typeof value === 'object') {
    const count = Object.keys(value).length;
    return `{${count} ${count === 1 ? 'key' : 'keys'}}`;
  }
  return formatMetadataValue(value, 24);
}

function compareEntries(
  [left, leftValue]: [string, unknown],
  [right, rightValue]: [string, unknown],
) {
  const leftIsInternal = left.startsWith(INTERNAL_METADATA_PREFIX);
  const rightIsInternal = right.startsWith(INTERNAL_METADATA_PREFIX);
  if (leftIsInternal !== rightIsInternal) {
    return Number(leftIsInternal) - Number(rightIsInternal);
  }

  const leftPriority = PRIORITY_KEYS.get(normalizeKey(left)) ?? Infinity;
  const rightPriority = PRIORITY_KEYS.get(normalizeKey(right)) ?? Infinity;
  if (leftPriority !== rightPriority) {
    return leftPriority - rightPriority;
  }

  const leftIsStructured = typeof leftValue === 'object' && leftValue !== null;
  const rightIsStructured =
    typeof rightValue === 'object' && rightValue !== null;
  if (leftIsStructured !== rightIsStructured) {
    return Number(leftIsStructured) - Number(rightIsStructured);
  }

  return left.toLowerCase().localeCompare(right.toLowerCase());
}

export function buildMetadataPreview(metadata: object, limit = 3) {
  const entries = Object.entries(metadata).sort(compareEntries);
  const visible = entries.slice(0, limit).map(([key, value]) => ({
    key,
    value,
    displayValue: formatMetadataPreviewValue(value),
  }));

  return {
    entries,
    remaining: Math.max(0, entries.length - visible.length),
    visible,
  };
}
